import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import EventQuestions, { validateAnswers } from "./EventQuestions";
import { AppOnly } from "../index";
import {
  api,
  clearCampaign,
  clearDraft,
  getToken,
  getUserId,
  isSoldOut,
  loadDraft,
  loadRazorpay,
  loginUrl,
  passDays,
  rupees,
  saveDraft,
  takeCampaign,
} from "../eventShared";
import "./styles.css"; // kept: other pages may rely on these global rules

// Web ticket checkout. Same flow and APIs as the app's CheckoutScreen:
// pick passes -> checkout-preview (server prices) -> /tickets/buy -> Razorpay -> webhook marks PAID.
// Visitors can pick passes; login happens at Pay (selection is kept across the login page).

const MAX_PER_PASS = 10;
const isGachaCode = (code) => code.toUpperCase().startsWith("GACHA-");

const normalisePhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 12 && digits.startsWith("91") ? digits.slice(2) : digits;
};

const EventCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("event") || localStorage.getItem("eventId");
  const checkoutUrl = `/events/checkout?event=${eventId}`;
  const loggedIn = !!getToken();

  const [event, setEvent] = useState(null);
  const [passes, setPasses] = useState([]);
  const [owned, setOwned] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const draft = useMemo(() => (eventId ? loadDraft(eventId) : null), [eventId]);
  const [qty, setQty] = useState(draft?.qty || {});
  const [slots, setSlots] = useState(draft?.slots || {});
  const [slotOptions, setSlotOptions] = useState({});
  const [phone, setPhone] = useState(draft?.phone || "");
  const [answers, setAnswers] = useState(draft?.answers || {});
  const [answerErrors, setAnswerErrors] = useState({});

  const [couponInput, setCouponInput] = useState(draft?.couponInput || "");
  // { code, isGacha, checked: [typeIds tried], valid: [typeIds that accept it] }
  const [coupon, setCoupon] = useState(null);
  const [couponMsg, setCouponMsg] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const previewSeq = useRef(0);

  const [payBusy, setPayBusy] = useState(false);
  const [payMsg, setPayMsg] = useState(null);
  const pendingOrder = useRef(null); // { orderId, razorpayOrderId, paid }

  const selected = passes.filter((p) => (qty[p.id] || 0) > 0);
  const totalQty = selected.reduce((sum, p) => sum + qty[p.id], 0);

  const goLogin = useCallback(
    (extra = {}) => {
      saveDraft(eventId, { qty, slots, phone, answers, couponInput, ...extra });
      navigate(loginUrl(checkoutUrl));
    },
    [eventId, qty, slots, phone, answers, couponInput, navigate, checkoutUrl],
  );

  // Load the event, its passes and (logged in) the passes this user already owns
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const load = async (retried = false) => {
      setLoading(true);
      setLoadError(null);
      try {
        const [productRes, typesRes] = await Promise.all([
          api(`/products/${eventId}`),
          api(`/tickets/ticketTypes/${eventId}`),
        ]);
        let ownedCounts = {};
        const userId = getUserId();
        if (getToken() && userId && (typesRes.data || []).some((t) => t.userLimitRestiction)) {
          const mine = await api(`/tickets/userTickets/${userId}/${eventId}`);
          ownedCounts = (mine.data || []).reduce((acc, t) => {
            acc[t.ticketTypeId] = (acc[t.ticketTypeId] || 0) + 1;
            return acc;
          }, {});
        }
        if (cancelled) return;
        setEvent(productRes.data.product);
        setPasses(typesRes.data || []);
        setOwned(ownedCounts);
      } catch (err) {
        if (cancelled) return;
        if (err.status === 401 && !retried) return load(true);
        setLoadError(err.status === 404 ? "This event is not available." : err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const limitFor = useCallback(
    (pass) => {
      const stock = Math.max(0, pass.ticketMaxQuantity - pass.ticketBoughtQuantity);
      const userLeft =
        pass.userLimitRestiction && pass.perUserLimit
          ? Math.max(0, pass.perUserLimit - (owned[pass.id] || 0))
          : Infinity;
      return Math.min(stock, userLeft, MAX_PER_PASS);
    },
    [owned],
  );

  // Per-booking cap (e.g. 4 seats), set on the event by the admin and sent with each pass
  const perOrderCap = passes.find((p) => p.maxTicketsPerOrder)?.maxTicketsPerOrder || Infinity;
  const eventLeft = event ? Math.max(0, Math.min(event.maxLimit ?? Infinity, perOrderCap)) : Infinity;

  const changeQty = (pass, delta) => {
    setPayMsg(null);
    setQty((prev) => {
      const current = prev[pass.id] || 0;
      const next = Math.max(0, Math.min(current + delta, limitFor(pass)));
      if (delta > 0 && totalQty + 1 > eventLeft) return prev;
      return { ...prev, [pass.id]: next };
    });
  };

  // Drop selections that are no longer allowed (sold out, limit reached, pass removed)
  useEffect(() => {
    if (!passes.length) return;
    setQty((prev) => {
      const next = {};
      passes.forEach((p) => {
        const n = Math.min(prev[p.id] || 0, limitFor(p));
        if (n > 0) next[p.id] = n;
      });
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  }, [passes, limitFor]);

  // Time slots, only for passes that have them (needs a login)
  const slotsRequested = useRef(new Set());
  const loadSlots = useCallback(async (p) => {
    try {
      const res = await api(`/tickets/timeslots/available/${p.id}`);
      setSlotOptions((prev) => ({ ...prev, [p.id]: res.data || [] }));
    } catch {
      setSlotOptions((prev) => ({ ...prev, [p.id]: [] }));
    }
  }, []);

  useEffect(() => {
    if (!loggedIn) return;
    selected
      .filter((p) => p.hasTimeslots && !slotsRequested.current.has(p.id))
      .forEach((p) => {
        slotsRequested.current.add(p.id);
        loadSlots(p);
      });
  }, [loggedIn, selected, loadSlots]);

  // A slot filled up while the buyer was choosing: reload the lists and clear
  // the picked slots so they choose again from what is really left
  const refreshSlots = () => {
    selected.filter((p) => p.hasTimeslots).forEach(loadSlots);
    setSlots({});
  };

  // Promo codes can be limited to some pass types: try the code on each selected type,
  // like the app, and attach it only where it is accepted.
  const tryPromo = async (code, typeIds) => {
    const results = await Promise.all(
      typeIds.map((ticketTypeId) =>
        api("/tickets/checkout-preview", {
          method: "POST",
          body: { productId: eventId, ticketsData: [{ ticketTypeId, couponCode: code }] },
        }).then(
          () => ({ ticketTypeId, ok: true }),
          (err) => ({ ticketTypeId, ok: false, message: err.message, status: err.status }),
        ),
      ),
    );
    if (results.some((r) => r.status === 401)) throw Object.assign(new Error("login"), { status: 401 });
    return results;
  };

  const applyCoupon = async (rawCode = couponInput) => {
    const code = rawCode.trim().toUpperCase();
    setCouponMsg(null);
    if (!code) return;
    if (!selected.length) {
      setCouponMsg({ ok: false, text: "Pick a pass first." });
      return;
    }
    if (!getToken()) {
      goLogin({ couponInput: code, pendingCoupon: true });
      return;
    }
    setCouponBusy(true);
    try {
      if (isGachaCode(code)) {
        const ticketsData = selected.flatMap((p) => Array(qty[p.id]).fill({ ticketTypeId: p.id }));
        await api("/tickets/checkout-preview", {
          method: "POST",
          body: { productId: eventId, ticketsData, couponCode: code },
        });
        setCoupon({ code, isGacha: true, checked: [], valid: [] });
        setCouponMsg({ ok: true, text: `${code} applied.` });
        return;
      }
      const results = await tryPromo(code, selected.map((p) => p.id));
      const valid = results.filter((r) => r.ok).map((r) => r.ticketTypeId);
      if (!valid.length) {
        const reasons = [...new Set(results.map((r) => r.message).filter(Boolean))];
        setCoupon(null);
        setCouponMsg({ ok: false, text: reasons.join(" ") || "This code can't be used for these passes." });
        return;
      }
      setCoupon({ code, isGacha: false, checked: selected.map((p) => p.id), valid });
      setCouponMsg({
        ok: true,
        text:
          valid.length === selected.length
            ? `${code} applied.`
            : `${code} applied to some passes only.`,
      });
    } catch (err) {
      if (err.status === 401) return goLogin({ couponInput: code, pendingCoupon: true });
      setCoupon(null);
      setCouponMsg({ ok: false, text: err.message });
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput("");
    setCouponMsg(null);
  };

  // Back from login with a code the visitor wanted to apply
  const pendingCouponDone = useRef(false);
  useEffect(() => {
    if (pendingCouponDone.current || !draft?.pendingCoupon || !loggedIn || !passes.length) return;
    pendingCouponDone.current = true;
    applyCoupon(draft.couponInput || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passes, loggedIn]);

  // A promo code applied earlier: check it on pass types added since
  useEffect(() => {
    if (!coupon || coupon.isGacha) return;
    const unchecked = selected.map((p) => p.id).filter((id) => !coupon.checked.includes(id));
    if (!unchecked.length) return;
    tryPromo(coupon.code, unchecked)
      .then((results) => {
        setCoupon((prev) =>
          prev && prev.code === coupon.code
            ? {
                ...prev,
                checked: [...prev.checked, ...unchecked],
                valid: [...prev.valid, ...results.filter((r) => r.ok).map((r) => r.ticketTypeId)],
              }
            : prev,
        );
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupon, totalQty]);

  const ticketsData = useMemo(
    () =>
      selected.flatMap((p) =>
        Array.from({ length: qty[p.id] }, () => ({
          ticketTypeId: p.id,
          ...(coupon && !coupon.isGacha && coupon.valid.includes(p.id) ? { couponCode: coupon.code } : {}),
        })),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(qty), passes, coupon],
  );

  // Server prices for the current selection (logged in only; the endpoint needs the buyer)
  useEffect(() => {
    setPreviewError(null);
    if (!loggedIn || !ticketsData.length) {
      setPreview(null);
      return;
    }
    const seq = ++previewSeq.current;
    setPreviewBusy(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api("/tickets/checkout-preview", {
          method: "POST",
          body: {
            productId: eventId,
            ticketsData,
            ...(coupon?.isGacha ? { couponCode: coupon.code } : {}),
          },
        });
        if (seq === previewSeq.current) setPreview(res.data);
      } catch (err) {
        if (seq !== previewSeq.current) return;
        if (err.status === 401) return goLogin();
        setPreview(null);
        setPreviewError(err.message);
      } finally {
        if (seq === previewSeq.current) setPreviewBusy(false);
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, ticketsData, eventId]);

  const estimate = selected.reduce((sum, p) => sum + Number(p.totalPrice) * qty[p.id], 0);
  const total = preview ? preview.pricing.subtotalBeforePoints : estimate;
  const isFree = !!preview && preview.pricing.subtotalBeforePoints === 0;

  const cancelPending = async () => {
    const order = pendingOrder.current;
    if (!order || order.paid) return;
    pendingOrder.current = null;
    try {
      await api("/payment/cancelOrder", {
        method: "POST",
        body: { orderType: "TICKET_ORDER", razorpayOrderId: order.razorpayOrderId },
      });
    } catch {
      // The order stays PENDING; a late payment is still matched by the webhook
    }
  };

  const finish = (orderId) => {
    clearDraft(eventId);
    clearCampaign();
    navigate(`/events/order-confirmed?order=${orderId}`);
  };

  const pay = async () => {
    setPayMsg(null);
    if (!totalQty) return setPayMsg("Pick at least one pass.");
    if (!getToken()) return goLogin();

    const phoneNumber = normalisePhone(phone);
    if (phoneNumber.length !== 10) return setPayMsg("Enter a 10-digit mobile number.");

    const errors = validateAnswers(event.questions, answers);
    setAnswerErrors(errors);
    if (Object.keys(errors).length) return setPayMsg("Please answer the required questions.");

    const missingSlot = selected.find((p) => p.hasTimeslots && !slots[p.id]);
    if (missingSlot) return setPayMsg(`Pick a time slot for ${missingSlot.ticketType}.`);
    if (previewError) return setPayMsg(previewError);

    setPayBusy(true);
    try {
      if (!isFree && !(await loadRazorpay())) {
        throw new Error("The payment window could not load. Check your connection and try again.");
      }

      const questionAnswers = Object.entries(answers).map(([questionId, answer]) => ({ questionId, answer }));
      const campaign = takeCampaign(eventId);
      const res = await api("/tickets/buy", {
        method: "POST",
        body: {
          productId: eventId,
          phoneNumber,
          ticketsData: ticketsData.map((t) => ({
            ...t,
            questionAnswers,
            pointsUsed: 0,
            ...(slots[t.ticketTypeId] ? { timeslotId: slots[t.ticketTypeId] } : {}),
          })),
          ...(coupon?.isGacha ? { userCouponId: coupon.code } : {}),
          metadata: campaign
            ? { source: campaign.source, campaign: campaign.campaign, channel: "web" }
            : { source: "web" },
        },
      });
      const data = res.data;

      if (!data.needsPayment) return finish(data.orderId);

      const rp = data.razorpay;
      pendingOrder.current = { orderId: data.orderId, razorpayOrderId: rp.orderId, paid: false };
      const checkout = new window.Razorpay({
        key: rp.key || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: rp.amount,
        currency: rp.currency || "INR",
        order_id: rp.orderId,
        name: "Ōta LIFE",
        description: event.title,
        prefill: { contact: phoneNumber },
        theme: { color: "#3969E7" },
        handler: () => {
          // The Razorpay webhook marks the order PAID; the confirmation page waits for it
          if (pendingOrder.current) pendingOrder.current.paid = true;
          finish(data.orderId);
        },
        modal: {
          ondismiss: async () => {
            if (pendingOrder.current?.paid) return;
            await cancelPending();
            setPayBusy(false);
            setPayMsg("Payment cancelled. No money was taken. You can try again.");
          },
        },
      });
      checkout.open();
    } catch (err) {
      setPayBusy(false);
      if (err.status === 401) return goLogin();
      if (/time ?slot|sold out for the selected|seat\(s\) left/i.test(err.message || "")) refreshSlots();
      setPayMsg(err.message);
    }
  };

  if (!eventId) {
    return (
      <Centered>
        <p className="text-lg font-semibold text-gray-800">No event selected.</p>
        <Link to="/" className="font-semibold text-[#3969E7]">
          Go to the home page
        </Link>
      </Centered>
    );
  }

  if (loading) return <Centered><p className="text-gray-500">Loading checkout...</p></Centered>;

  if (loadError || !event) {
    return (
      <Centered>
        <p className="text-lg font-semibold text-gray-800">{loadError || "Event not found."}</p>
        <Link to="/" className="font-semibold text-[#3969E7]">
          Go to the home page
        </Link>
      </Centered>
    );
  }

  const pricing = preview?.pricing;
  const discount = pricing ? pricing.promoCouponDiscount + pricing.gachaCouponDiscount : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F3FF] to-white" data-testid="event-checkout">
      <div className="mx-auto max-w-xl px-4 pb-32 pt-5">
        <Link to={`/events/${eventId}`} className="text-sm text-[#3969E7]">
          ‹ Event details
        </Link>
        <h1 className="mt-2 text-xl font-extrabold text-gray-900">{event.title}</h1>

        {event.isBooking ? (
          <AppOnly />
        ) : (
          <>
            <Section title="Choose passes">
              {perOrderCap !== Infinity && (
                <p className="mb-2 text-xs text-gray-600" data-testid="per-order-note">
                  Up to {perOrderCap} per booking.
                </p>
              )}
              <ul className="space-y-2" data-testid="checkout-passes">
                {passes.map((pass) => {
                  const soldOut = isSoldOut(pass);
                  const limit = limitFor(pass);
                  const n = qty[pass.id] || 0;
                  const days = passDays(pass);
                  return (
                    <li key={pass.id} className={`rounded-xl border bg-white p-3 ${soldOut ? "opacity-60" : ""}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900">{pass.ticketType}</p>
                          {days && <p className="text-xs text-gray-500">{days}</p>}
                          <p className="text-sm font-bold text-gray-900">{rupees(pass.totalPrice)}</p>
                          {pass.activityDescription && (
                            <p className="mt-1 whitespace-pre-line text-xs text-gray-600" data-testid={`pass-note-${pass.id}`}>
                              {pass.activityDescription}
                            </p>
                          )}
                        </div>
                        {soldOut ? (
                          <span className="rounded-full bg-gray-200 px-3 py-1 text-xs font-semibold text-gray-600">Sold out</span>
                        ) : limit === 0 ? (
                          <span className="text-xs text-gray-500">Limit reached</span>
                        ) : (
                          <div className="flex items-center gap-3">
                            <QtyButton label="−" disabled={n === 0} onClick={() => changeQty(pass, -1)} testId={`minus-${pass.id}`} />
                            <span className="w-5 text-center font-semibold" data-testid={`qty-${pass.id}`}>{n}</span>
                            <QtyButton label="+" disabled={n >= limit || totalQty >= eventLeft} onClick={() => changeQty(pass, 1)} testId={`plus-${pass.id}`} />
                          </div>
                        )}
                      </div>
                      {n > 0 && pass.hasTimeslots && (
                        <div className="mt-2">
                          {!loggedIn ? (
                            <p className="text-xs text-gray-500">You can pick a time slot after logging in.</p>
                          ) : (
                            <select
                              className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                              value={slots[pass.id] || ""}
                              onChange={(e) => setSlots((prev) => ({ ...prev, [pass.id]: e.target.value }))}
                            >
                              <option value="">Pick a time slot</option>
                              {(slotOptions[pass.id] || [])
                                .filter((s) => s.availableCapacity >= n)
                                .map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {new Date(s.startTime).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" })}
                                    {` (${s.availableCapacity} left)`}
                                  </option>
                                ))}
                            </select>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-2 text-xs text-gray-400">Prices include GST.</p>
            </Section>

            {passes.some((p) => (qty[p.id] || 0) > 0 && p.activityDescription) && (
              <Section title="Before you book">
                <div className="space-y-2" data-testid="booking-rules">
                  {passes
                    .filter((p) => (qty[p.id] || 0) > 0 && p.activityDescription)
                    .map((p) => (
                      <div key={p.id} className="rounded-xl border-l-4 border-amber-500 bg-amber-50 p-3">
                        <p className="text-sm font-bold text-amber-800">{p.activityTitle ? `${p.ticketType}: ${p.activityTitle}` : p.ticketType}</p>
                        <p className="mt-1 whitespace-pre-line text-sm text-stone-700">{p.activityDescription}</p>
                      </div>
                    ))}
                </div>
              </Section>
            )}

            <Section title="Mobile number">
              <input
                type="tel"
                inputMode="numeric"
                className="w-full rounded-lg border border-gray-300 p-3"
                placeholder="10-digit mobile number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="checkout-phone"
              />
            </Section>

            {event.questions?.length > 0 && (
              <Section title="A few questions">
                <EventQuestions
                  questions={event.questions}
                  answers={answers}
                  errors={answerErrors}
                  onChange={(id, value) => setAnswers((prev) => ({ ...prev, [id]: value }))}
                />
              </Section>
            )}

            <Section title="Coupon or reward code">
              {coupon ? (
                <div className="flex items-center justify-between rounded-lg border border-green-300 bg-green-50 p-3 text-sm">
                  <span className="font-semibold text-green-800" data-testid="coupon-applied">{coupon.code}</span>
                  <button type="button" onClick={removeCoupon} className="font-semibold text-gray-600">
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 p-3 uppercase"
                    placeholder="Enter code"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                    data-testid="coupon-input"
                  />
                  <button
                    type="button"
                    disabled={couponBusy || !couponInput.trim()}
                    onClick={() => applyCoupon()}
                    className="rounded-lg bg-gray-900 px-4 font-semibold text-white disabled:bg-gray-400"
                    data-testid="coupon-apply"
                  >
                    {couponBusy ? "..." : "Apply"}
                  </button>
                </div>
              )}
              {couponMsg && (
                <p className={`mt-2 text-sm ${couponMsg.ok ? "text-green-700" : "text-red-600"}`} data-testid="coupon-msg">
                  {couponMsg.text}
                </p>
              )}
            </Section>

            {totalQty > 0 && (
              <Section title="Summary">
                <div className="space-y-1 text-sm text-gray-700" data-testid="checkout-summary">
                  {selected.map((p) => (
                    <Row key={p.id} label={`${p.ticketType} × ${qty[p.id]}`} value={rupees(Number(p.totalPrice) * qty[p.id])} />
                  ))}
                  {pricing && (
                    <>
                      <div className="my-2 border-t" />
                      <Row label="Price before GST" value={rupees(pricing.originalPrice)} />
                      {discount > 0 && <Row label="Discount" value={`− ${rupees(discount)}`} highlight />}
                      <Row label="GST" value={rupees(pricing.taxes)} />
                      {pricing.fees > 0 && <Row label="Fees" value={rupees(pricing.fees)} />}
                    </>
                  )}
                  <div className="my-2 border-t" />
                  <Row label={<b>Total</b>} value={<b data-testid="checkout-total">{previewBusy ? "..." : rupees(total)}</b>} />
                  {!loggedIn && <p className="text-xs text-gray-500">Log in at Pay to see your final price and use codes.</p>}
                  {previewError && <p className="text-sm text-red-600" data-testid="preview-error">{previewError}</p>}
                </div>
              </Section>
            )}
          </>
        )}
      </div>

      {!event.isBooking && (
        <div className="fixed inset-x-0 bottom-0 border-t bg-white/95 p-4 backdrop-blur">
          <div className="mx-auto max-w-xl">
            {payMsg && <p className="mb-2 text-center text-sm text-red-600" data-testid="pay-msg">{payMsg}</p>}
            <button
              type="button"
              disabled={payBusy || totalQty === 0 || (loggedIn && previewBusy)}
              onClick={pay}
              className="block w-full rounded-xl bg-gradient-to-r from-[#3969E7] to-[#7D2AE7] py-3 text-base font-bold text-white disabled:from-gray-400 disabled:to-gray-400"
              data-testid="checkout-pay"
            >
              {payBusy
                ? "Please wait..."
                : totalQty === 0
                ? "Pick a pass"
                : !loggedIn
                ? "Log in to continue"
                : isFree
                ? "Get passes"
                : `Pay ${rupees(total)}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Centered = ({ children }) => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#F5F3FF] px-6 text-center">{children}</div>
);

const Section = ({ title, children }) => (
  <section className="mt-6">
    <h2 className="mb-2 text-base font-bold text-gray-900">{title}</h2>
    {children}
  </section>
);

const Row = ({ label, value, highlight }) => (
  <div className={`flex justify-between gap-3 ${highlight ? "text-green-700" : ""}`}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

const QtyButton = ({ label, disabled, onClick, testId }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-lg font-bold text-gray-800 disabled:opacity-30"
    data-testid={testId}
    aria-label={label === "+" ? "Add one" : "Remove one"}
  >
    {label}
  </button>
);

export default EventCheckout;
